import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { isAuthenticated, login, apiRequest } from '@/lib/auth';
import { useT } from '@/lib/LanguageContext';
import { Loader2, Fish, Anchor, Scale, MessagesSquare } from 'lucide-react';
import { toast } from 'sonner';

const features = [
  { icon: Fish,           label: 'The Catch', desc: 'Contacts, leads and deals coming in' },
  { icon: Anchor,         label: 'The Stage', desc: 'Tasks, documents and the work itself' },
  { icon: Scale,          label: 'The Tally', desc: 'Signatures, invoices, what\u2019s settled' },
  { icon: MessagesSquare, label: 'The Yarns', desc: 'Conversations, interviews and campaigns' },
];

export default function Login() {
  const t = useT();
  const [loading, setLoading] = useState(false);
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated()) navigate('/dashboard');
  }, [navigate]);

  const forgot = async () => {
    if (!email.trim()) { toast.error('Enter your email first, then click Forgot your password.'); return; }
    try {
      await apiRequest('/api/auth/password-reset-request', { method: 'POST', body: JSON.stringify({ email: email.trim() }) });
      toast.success('If that email has a login, a reset link is on its way.');
    } catch (err) { toast.error('Could not send the reset email. Try again in a moment.'); }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login({ email, password });
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Login failed:', error);
      toast.error('Login failed', {
        description: error.message || 'Invalid email or password',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row" style={{ fontFamily: 'Manrope, sans-serif' }}>

      {/* ── Brand panel ─────────────────────────────────────────────────── */}
      <div
        className="relative flex flex-col md:w-[55%] px-10 py-12 overflow-hidden min-h-[560px]"
        style={{ backgroundColor: 'hsl(var(--sidebar))' }}
      >
        {/* Harbour scene fills the panel; the shade sits behind the words only */}
        <div aria-hidden="true" style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: '42%', minHeight: 240 }}>
          <img src="/harbour-scene.svg" alt=""
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: '50% 100%' }} />
          <div style={{ position: 'absolute', left: 0, right: 0, top: 0, height: '30%',
            background: 'linear-gradient(180deg, hsl(var(--sidebar)) 0%, rgba(22,34,44,0) 100%)' }} />
        </div>
        {/* Logo */}
        <div className="relative">
          <img src="/stagehead-os-lockup-dark.svg" alt="Stagehead OS" style={{ height: 52 }} />
        </div>

        {/* Hero copy */}
        <div className="relative flex flex-col pt-10" style={{ paddingBottom: 'max(42vh, 260px)' }}>
          <p style={{ fontSize: '0.8rem', fontWeight: 600, letterSpacing: '0.12em', color: '#F0B56E', textTransform: 'uppercase', marginBottom: '1rem' }}>
            Tie up, and everything&#8217;s there
          </p>
          <h1 className="font-display" style={{ fontSize: 'clamp(2rem, 3.4vw, 3rem)', lineHeight: 1.05, color: '#F6F1E7', marginBottom: '1.25rem' }}>
            Where the day&#8217;s<br />work lands.
          </h1>
          <p style={{ fontSize: '1rem', lineHeight: 1.65, color: 'rgba(255,255,255,0.72)', maxWidth: '380px', marginBottom: '2.5rem' }}>
            Clients, pipeline, tasks, campaigns and documents come in here and get put away properly, so your team can get on with the day.
          </p>

          {/* Feature list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {features.map(({ icon: Icon, label, desc }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon size={16} color="#F0B56E" />
                </div>
                <div>
                  <span style={{ fontSize: '0.875rem', fontWeight: 700, color: '#FFFFFF' }}>{label}</span>
                  <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.62)', marginLeft: '0.5rem' }}>{desc}</span>
                </div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', marginTop: '1.75rem' }}>
            More at{' '}
            <a href="https://stagehead.app" target="_blank" rel="noreferrer" style={{ color: '#F0B56E', textDecoration: 'none' }}>stagehead.app</a>
          </div>
        </div>

      </div>

      {/* ── Login panel ─────────────────────────────────────────────────── */}
      <div
        className="flex flex-col items-center justify-center md:w-[45%] px-8 py-12"
        style={{ backgroundColor: 'hsl(var(--background))' }}
      >
        <div style={{ width: '100%', maxWidth: '380px' }}>

          {/* Logo + Heading */}
          <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
            <img src="/stagehead-os-lockup-light.svg" alt="Stagehead OS" style={{ height: 40, margin: '0 auto 1.75rem' }} />
            <h2 className="font-display" style={{ fontSize: '1.75rem', color: 'hsl(var(--foreground))', marginBottom: '0.4rem' }}>
              {t('auth.login_title')}
            </h2>
            <p style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>
              {t('auth.login_desc')}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <Label htmlFor="password">{t('auth.password')}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              size="lg"
              className="w-full"
              style={{ backgroundColor: 'hsl(var(--accent))', color: '#fff', fontWeight: 700, marginTop: '0.25rem' }}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  {t('auth.signing_in')}
                </>
              ) : (
                t('auth.sign_in')
              )}
            </Button>
          </form>

          <div style={{ textAlign: 'center', marginTop: '1rem' }}>
            <button type="button" onClick={forgot} style={{ fontSize: '0.8rem', fontWeight: 600, color: 'hsl(var(--accent))', background: 'none', border: 0, cursor: 'pointer' }}>
              Forgot your password?
            </button>
          </div>
          <p style={{ fontSize: '0.75rem', textAlign: 'center', color: 'hsl(var(--muted-foreground))', marginTop: '1.5rem', lineHeight: 1.6 }}>
            For team members. Ask your admin for access.
            <br />
            Working with a team as a client?{' '}
            <a href="/client/login" style={{ color: 'hsl(var(--accent))', fontWeight: 600, textDecoration: 'none' }}>Sign in to your client portal</a>
          </p>
        </div>
      </div>
    </div>
  );
}
